import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { cyan, deepOrange, lime, red } from '@mui/material/colors';
import SituacaoIdDescricaoBase from './SituacaoIdDescricaoBase';

/**
 * Enum para meios de julgamento de processos.
 *
 * @readonly
 * @enum {Object}
 */
export const MEIO_JULGAMENTO_PROCESSO = {
    VIRTUAL: {
        id: 'VIRTUAL',
        descricao: 'Julgamento virtual',
        descricaoCurta: 'Virtual',
        codigo: 'VIRTUAL',
        abreviacao: 'V',
        cor: lime[700]
    },
    PRESENCIAL: {
        id: 'PRESENCIAL',
        descricao: `Julgamento em sessão presencial`, // `Julgamento em sessão em tempo real (presencial/telepresencial)` // COVID-19
        descricaoCurta: 'Presencial',
        codigo: 'PRESENCIAL',
        abreviacao: 'P',
        cor: cyan[800]
    },
    REMETIDO_PARA_SESSAO_VINCULADA: {
        id: 'REMETIDO_PARA_SESSAO_VINCULADA',
        descricao: `Julgamento em sessão presencial (remetido da virtual vinculada)`,
        descricaoCurta: 'Presencial (remetido da virtual)',
        codigo: 'REMETIDO_PARA_PRESENCIAL',
        abreviacao: 'PR',
        cor: cyan[800]
    },
    VIRTUAL_REMETIDO: {
        id: 'VIRTUAL_REMETIDO',
        descricao: 'Julgamento virtual (remetido para sessão futura)',
        descricaoCurta: 'Virtual (remetido para sessão futura)',
        codigo: 'VIRTUAL_REMETIDO',
        abreviacao: 'VR',
        cor: deepOrange[700]
    }
};

/**
 * @classdesc Facilita o acesso às propriedades e valores da enum de meios de julgamento de processos.
 * @readonly
 */
export default class MeioJulgamentoProcesso {
    static idDescricaoBase = new SituacaoIdDescricaoBase(MEIO_JULGAMENTO_PROCESSO);

    static getDescricao(id) {
        return MeioJulgamentoProcesso.idDescricaoBase.getDescricao(id);
    }

    static getCodigo(id) {
        return MeioJulgamentoProcesso.idDescricaoBase.getValorPropriedade(id, 'codigo');
    }

    static getDescricaoCurta(id) {
        return MeioJulgamentoProcesso.idDescricaoBase.getValorPropriedade(id, 'descricaoCurta');
    }

    static getCor(id) {
        return MeioJulgamentoProcesso.idDescricaoBase.getValorPropriedade(id, 'cor');
    }

    static getItemByCodigo(codigo) {
        return MeioJulgamentoProcesso.idDescricaoBase.getObjectByProperty('codigo', codigo);
    }

    static getItemByCodigoAbreviado(codigo) {
        return MeioJulgamentoProcesso.idDescricaoBase.getObjectByProperty('abreviacao', codigo);
    }

    static getIdByCodigo(codigo) {
        const obj = MeioJulgamentoProcesso.getItemByCodigo(codigo);
        return obj ? obj.id : '';
    }

    static getDescricaoByCodigo(codigo) {
        return MeioJulgamentoProcesso.getDescricao(MeioJulgamentoProcesso.getIdByCodigo(codigo));
    }

    static getDescricaoCurtaByCodigo(codigo) {
        return MeioJulgamentoProcesso.getDescricaoCurta(MeioJulgamentoProcesso.getIdByCodigo(codigo));
    }

    static getCorByCodigo(codigo) {
        return MeioJulgamentoProcesso.getCor(MeioJulgamentoProcesso.getIdByCodigo(codigo));
    }

    static getIcone(id, tamanhoIcone = '26px') {
        const obj = MeioJulgamentoProcesso.idDescricaoBase.getObjectById(id);
        let abreviacao = obj?.abreviacao;
        let cor = obj?.cor;

        if (!abreviacao) {
            abreviacao = '?';
            cor = red[800];
        }

        return (
            <Avatar
                style={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: tamanhoIcone,
                    height: tamanhoIcone,
                    minWidth: tamanhoIcone,
                    minHeight: tamanhoIcone,
                    fontWeight: '500',
                    fontSize: abreviacao.length > 1 ? '0.75rem' : '0.96rem',
                    backgroundColor: cor
                }}
            >
                {abreviacao}
            </Avatar>
        );
    }

    static getIconeByCodigo(codigo, tamanhoIcone) {
        const id = this.getIdByCodigo(codigo);
        return this.getIcone(id, tamanhoIcone);
    }

    static getIconesLista(codigos) {
        if (Array.isArray(codigos)) {
            return (
                <Box display="flex">
                    {codigos.map((codigoMeioJulgamento) => (
                        <Box
                            component="span"
                            sx={{ mr: 1 }}
                            key={codigoMeioJulgamento}
                        >
                            {this.getIconeByCodigo(codigoMeioJulgamento)}
                        </Box>
                    ))}
                </Box>
            );
        }

        return null;
    }
}
